import React, {
  useState,
  useEffect,
} from 'react';
import {
  useNavigate,
  useLocation,
} from 'react-router';
import {
  DateTime,
} from 'luxon';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  coreUtils,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  decodeParam,
} from 'web-utils';

import {
  Button,
  Select,
  Spinner,
  NavigationTabs,
  ContentWrapper,
  CreateInvoiceModal,
} from 'web-components';

import {
  Text,
  Flex,
  SVG,
  TextBreadcrumbs,
} from 'web-styled';

import EmptyState from '../EmptyState';
import { getAmount, getTabs, fakeInvoices } from '../utils';

import * as S from './styled';

const mapState = (state => ({
  invoices: coreSelectors.getInvoices(state),
  searchOptions: coreSelectors.getSearchOptions(
    'clients',
  )(state),
  getInvoicesState: coreSelectors.getPaymentRequestsState(
    'getInvoicesState',
  )(state),
}));

type Query = {
  client?: string;
};

type Props = {
  isFake?: boolean,
};

const Invoices = ({ isFake = false }: Props) => {
  const [createInvoiceVisible, setCreateInvoiceVisible] = useState<boolean>(false);
  const {
    invoices,
    searchOptions,
    getInvoicesState,
  } = useSelector(mapState, shallowEqual);
  const query: Query = decodeParam(useLocation().search);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isFake) {
      dispatch(
        coreActions.requestInvoices({
          qs: {
            page: 1,
            per_page: 50,
            sort_direction: 'DESC',
            ...(query.client
              ? {
                client_id: query.client,
              }
              : {}
            ),
          },
        }),
      );
    }
  }, [query.client]);

  if (!isFake && getInvoicesState === 'pending') {
    return (
      <Spinner
        message="Loading invoices..."
      />
    );
  }

  const items = isFake ? fakeInvoices : invoices;

  return (
    <ContentWrapper
      header={(
        <Flex
          width="100%"
          height="50px"
          align="center"
          justify="space-between"
        >
          <Flex spacing={10}>
            <TextBreadcrumbs>
              Billing
            </TextBreadcrumbs>

            <Button
              icon={<SVG.Plus size={14} strokeWidth={4} />}
              appearance="success"
              onClick={() => setCreateInvoiceVisible(true)}
            >
              Create Invoice
            </Button>
          </Flex>
          <NavigationTabs
            tabs={getTabs()}
          />
        </Flex>
      )}
    >
      <Flex fillSpace column>
        {(items.length > 0) ? (
          <>
            <S.Search>
              <Select
                value={query.client
                  ? searchOptions.find(option => option.value === query.client)
                  : ''}
                options={searchOptions}
                isClearable
                placeholder="Search invoices by client"
                styles={{
                  menuList: (provided: any) => ({
                    ...provided,
                    maxHeight: '316px',
                  }),
                  control: (provided: any) => ({
                    ...provided,
                    boxShadow: 'none',
                    borderColor: 'hsl(0,0%,80%)',
                  }),
                }}
                onChange={(client: any) => {
                  if (!isFake) {
                    navigate({
                      pathname: '/billing/invoices',
                      search: client?.value ? `?client=${client.value}` : '',
                    });
                  }
                }}
              />
            </S.Search>
            <S.Item padding="10px 15px">
              <Flex
                align="center"
                width="10%"
                shrink="0"
              >
                <S.HeaderName>Status</S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="20%"
                shrink="0"
              >
                <S.HeaderName>Client</S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="20%"
                shrink="0"
              >
                <S.HeaderName>Product Name</S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="20%"
                shrink="0"
              >
                <S.HeaderName>Date Requested</S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="20%"
                shrink="0"
              >
                <S.HeaderName>Date Paid</S.HeaderName>
              </Flex>
              <Flex
                align="center"
                width="10%"
                shrink="0"
              >
                <S.HeaderName>Amount</S.HeaderName>
              </Flex>
            </S.Item>
            <Flex column scrollY fillSpace>
              {items.map(invoice => (
                <S.Item
                  key={invoice.id}
                  align="center"
                  padding="15px"
                >
                  <Flex
                    align="center"
                    width="10%"
                    shrink="0"
                  >
                    <S.Status status={invoice.status}>
                      {invoice.status === 'draft' ? 'open' : invoice.status}
                    </S.Status>
                  </Flex>
                  <Flex
                    align="center"
                    width="20%"
                    shrink="0"
                    spacing={10}
                  >
                    <Text size="14px" color="link">
                      {invoice.customer_name || invoice.account_name}
                    </Text>
                  </Flex>
                  <Flex
                    align="center"
                    width="20%"
                    shrink="0"
                  >
                    <Text size="14px" color="#9ea0a1">
                      {invoice.normalizedDescription}
                    </Text>
                  </Flex>
                  <Flex
                    align="center"
                    width="20%"
                    shrink="0"
                  >
                    <Text size="14px" color="#9ea0a1">
                      {typeof invoice.created === 'number'
                        ? DateTime.fromSeconds(invoice.created).toFormat('dd MMM yyyy')
                        : DateTime.fromISO(invoice.created).toFormat('dd MMM yyyy')}
                    </Text>
                  </Flex>
                  <Flex
                    align="center"
                    width="20%"
                    shrink="0"
                  >
                    <Text size="14px" color="#9ea0a1">
                      {invoice.paid_at ? DateTime.fromISO(invoice.paid_at).toFormat('dd MMM yyyy') : '-'}
                    </Text>
                  </Flex>
                  <Flex
                    align="center"
                    width="10%"
                    shrink="0"
                  >
                    <Text size="14px" color="#9ea0a1">
                      {coreUtils.getCurrencySymbol(invoice.currency) || '$'}
                      {`${invoice.total ? getAmount(invoice.total / 100) : 0}`}
                    </Text>
                  </Flex>
                </S.Item>
              ))}
            </Flex>
          </>
        ) : (
          <EmptyState
            text="
              Invoicing allows you to bill a client directly via email. <br />
              Try sending your first invoice now."
            buttonText="Create Invoice"
            onClick={() => setCreateInvoiceVisible(true)}
          />
        )}
      </Flex>

      <CreateInvoiceModal
        isOpen={createInvoiceVisible}
        onRequestCreate={async ({ description, ...rest }) => {
          await new Promise((resolve, reject) => {
            dispatch(
              coreActions.requestCreateInvoice({
                payload: {
                  ...rest,
                  ...(description ? { description } : {}),
                },
                callbacks: {
                  onError: reject,
                  onSuccess: resolve,
                },
              }),
            );
          });
        }}
        onRequestClose={() => setCreateInvoiceVisible(false)}
      />
    </ContentWrapper>
  );
};

export default Invoices;
